import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { getApiUrl } from '@/lib/api';
import { ADMIN_PLAN_OPTIONS } from '@/lib/pricing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog, AlertDialog } from '@/components/ConfirmDialog';
import { InvestorVerificationPanel } from '@/components/InvestorVerificationPanel';
import { AnalyticsPage } from '@/pages/crm/AnalyticsPage';
import { 
  Users, 
  UserCheck, 
  Shield, 
  BadgeCheck,
  CreditCard,
  Search,
  CheckCircle,
  XCircle,
  Trash2,
  Loader2,
  BarChart3,
  Clock,
  Key,
  Crown,
  Ban,
  MoreHorizontal,
  Building2,
  MapPin,
  Phone,
  Globe,
  Briefcase,
  DollarSign,
  Flag,
  Star,
  RefreshCw
} from 'lucide-react';

interface User {
  id: number;
  email: string;
  name: string;
  isAdmin: boolean;
  isApproved: boolean;
  isSubscribed: boolean;
  subscriptionPlan?: string;
  subscriptionExpiresAt?: string;
  createdAt: string;
  lastLoginAt: string;
}

interface Stats {
  totalUsers: number;
  pendingApprovals: number;
  totalAdmins: number;
  subscribedUsers: number;
  newUsersToday: number;
  failedLoginsToday: number;
}

interface OnboardingUser {
  id: number;
  name: string;
  email: string;
  createdAt: string;
  profile: {
    role: 'founder' | 'investor';
    companyName?: string;
    firmName?: string;
    website?: string;
    location?: string;
    phone?: string;
    stage?: string;
    fundingAmount?: string;
    investorType?: string;
    checkSize?: string;
    industries: string[];
    stages?: string[];
    businessDescription?: string;
    createdAt: string;
  } | null;
}

// Onboarding Tab Component
function OnboardingTab({
  users,
  filters,
  setFilters,
  selectedUser,
  setSelectedUser,
  isLoading
}: {
  users: OnboardingUser[];
  filters: { role: string; industry: string; location: string; hasOnboarding: string };
  setFilters: (f: typeof filters) => void;
  selectedUser: OnboardingUser | null;
  setSelectedUser: (u: OnboardingUser | null) => void;
  isLoading: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUsers = users.filter(u => 
    searchQuery === '' || 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.profile?.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.profile?.firmName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={filters.role}
          onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          className="px-3 py-2 rounded-lg border bg-background text-sm"
        >
          <option value="all">All Roles</option>
          <option value="founder">Founder</option>
          <option value="investor">Investor</option>
        </select>
        <select
          value={filters.hasOnboarding}
          onChange={(e) => setFilters({ ...filters, hasOnboarding: e.target.value })}
          className="px-3 py-2 rounded-lg border bg-background text-sm"
        >
          <option value="all">All Users</option>
          <option value="true">Completed Onboarding</option>
          <option value="false">Not Started</option>
        </select>
        <Input
          placeholder="Filter by industry..."
          value={filters.industry}
          onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
          className="w-40"
        />
        <Input
          placeholder="Filter by location..."
          value={filters.location}
          onChange={(e) => setFilters({ ...filters, location: e.target.value })}
          className="w-40"
        />
      </div>

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredUsers.length} of {users.length} users
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users Table */}
        <div className="lg:col-span-2 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No users found matching your filters.
            </div>
          ) : (
            filteredUsers.map(user => (
              <div
                key={user.id}
                onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                  selectedUser?.id === user.id
                    ? 'border-primary bg-primary/5'
                    : 'hover:bg-muted/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{user.name || 'Unnamed User'}</h4>
                      {user.profile ? (
                        <Badge variant="default" className="bg-green-100 text-green-700 hover:bg-green-100">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Onboarded
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-200">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                    {user.profile && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="secondary">
                          {user.profile.role === 'founder' ? 'Founder' : 'Investor'}
                        </Badge>
                        {user.profile.companyName && (
                          <Badge variant="outline">{user.profile.companyName}</Badge>
                        )}
                        {user.profile.firmName && (
                          <Badge variant="outline">{user.profile.firmName}</Badge>
                        )}
                        {user.profile.location && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            📍 {user.profile.location}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          {selectedUser ? (
            <div className="sticky top-4 space-y-4">
              <div className="p-4 rounded-xl border bg-card">
                <h3 className="font-semibold mb-4">User Details</h3>
                
                {/* Contact Info */}
                <div className="space-y-3 mb-6">
                  <div>
                    <label className="text-xs text-muted-foreground">Email</label>
                    <p className="text-sm font-medium">{selectedUser.email}</p>
                  </div>
                  {selectedUser.profile?.phone && (
                    <div>
                      <label className="text-xs text-muted-foreground">Phone</label>
                      <p className="text-sm font-medium">{selectedUser.profile.phone}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-muted-foreground">Signup Date</label>
                    <p className="text-sm">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>

                {selectedUser.profile ? (
                  <div className="space-y-4">
                    <div className="pt-4 border-t">
                      <h4 className="text-sm font-medium mb-3">Profile Information</h4>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-muted-foreground">Role</label>
                          <Badge className="ml-2">
                            {selectedUser.profile.role === 'founder' ? 'Founder' : 'Investor'}
                          </Badge>
                        </div>
                        
                        {selectedUser.profile.role === 'founder' ? (
                          <>
                            {selectedUser.profile.companyName && (
                              <div>
                                <label className="text-xs text-muted-foreground">Company</label>
                                <p className="text-sm">{selectedUser.profile.companyName}</p>
                              </div>
                            )}
                            {selectedUser.profile.stage && (
                              <div>
                                <label className="text-xs text-muted-foreground">Stage</label>
                                <p className="text-sm">{selectedUser.profile.stage}</p>
                              </div>
                            )}
                            {selectedUser.profile.fundingAmount && (
                              <div>
                                <label className="text-xs text-muted-foreground">Funding Need</label>
                                <p className="text-sm">{selectedUser.profile.fundingAmount}</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {selectedUser.profile.firmName && (
                              <div>
                                <label className="text-xs text-muted-foreground">Firm</label>
                                <p className="text-sm">{selectedUser.profile.firmName}</p>
                              </div>
                            )}
                            {selectedUser.profile.investorType && (
                              <div>
                                <label className="text-xs text-muted-foreground">Investor Type</label>
                                <p className="text-sm">{selectedUser.profile.investorType}</p>
                              </div>
                            )}
                            {selectedUser.profile.checkSize && (
                              <div>
                                <label className="text-xs text-muted-foreground">Check Size</label>
                                <p className="text-sm">{selectedUser.profile.checkSize}</p>
                              </div>
                            )}
                          </>
                        )}
                        
                        {selectedUser.profile.location && (
                          <div>
                            <label className="text-xs text-muted-foreground">Location</label>
                            <p className="text-sm">{selectedUser.profile.location}</p>
                          </div>
                        )}
                        
                        {selectedUser.profile.website && (
                          <div>
                            <label className="text-xs text-muted-foreground">Website</label>
                            <a 
                              href={selectedUser.profile.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline block truncate"
                            >
                              {selectedUser.profile.website}
                            </a>
                          </div>
                        )}
                        
                        {selectedUser.profile.industries?.length > 0 && (
                          <div>
                            <label className="text-xs text-muted-foreground">Industries</label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedUser.profile.industries.map((ind: string) => (
                                <Badge key={ind} variant="outline" className="text-xs">
                                  {ind}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {selectedUser.profile.stages?.length > 0 && (
                          <div>
                            <label className="text-xs text-muted-foreground">Investment Stages</label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedUser.profile.stages.map((stage: string) => (
                                <Badge key={stage} variant="outline" className="text-xs">
                                  {stage}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {selectedUser.profile.businessDescription && (
                          <div>
                            <label className="text-xs text-muted-foreground">Description</label>
                            <p className="text-sm mt-1">{selectedUser.profile.businessDescription}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <p className="text-sm">User has not completed onboarding yet.</p>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-4 border-t mt-4 space-y-2">
                  <Button className="w-full" size="sm">
                    Create Deal
                  </Button>
                  <Button variant="outline" className="w-full" size="sm">
                    Mark Priority
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="sticky top-4 p-8 rounded-xl border border-dashed text-center text-muted-foreground">
              <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select a user to view their onboarding details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminDashboard({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'investors' | 'analytics' | 'onboarding'>('overview');
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState<'all' | 'pending' | 'approved' | 'admins'>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [selectedPlanKey, setSelectedPlanKey] = useState<string>('starter');

  // Admin testing toggle: when on, admins lose their /matches pro bypass so they
  // can verify the gated flow a regular user with no plan would see.
  const [simulateNoPlan, setSimulateNoPlan] = useState(
    () => localStorage.getItem('backr_admin_simulate_no_plan') === 'true'
  );
  const toggleSimulateNoPlan = () => {
    setSimulateNoPlan((prev) => {
      const next = !prev;
      if (next) localStorage.setItem('backr_admin_simulate_no_plan', 'true');
      else localStorage.removeItem('backr_admin_simulate_no_plan');
      return next;
    });
  };


  // Onboarding tab state
  const [onboardingUsers, setOnboardingUsers] = useState<OnboardingUser[]>([]);
  const [onboardingFilters, setOnboardingFilters] = useState({
    role: 'all',
    industry: '',
    location: '',
    hasOnboarding: 'all'
  });
  const [selectedOnboardingUser, setSelectedOnboardingUser] = useState<OnboardingUser | null>(null);
  
  // Pre-select current plan when editing subscription
  useEffect(() => {
    if (showSubscriptionModal && selectedUser?.isSubscribed && selectedUser?.subscriptionPlan) {
      // Map DB-stored plan (including legacy labels) to a PLAN_PRICING key.
      const aliases: Record<string, string> = {
        'Starter': 'starter',
        'Fundraise Plan': 'fundraise',
        'Pro Plan': 'pro',
        '3-day': 'playtest',
        '3 Day Access': 'playtest',
        '7-day': 'starter',
        '7 Day Access': 'starter',
        '1-month': 'starter',
        '1 Month Access': 'starter',
        '6-month': 'fundraise',
        '6 Month Access': 'fundraise',
        '1-year': 'pro',
        '1 Year Access': 'pro',
      };
      const key = aliases[selectedUser.subscriptionPlan] ?? selectedUser.subscriptionPlan;
      setSelectedPlanKey(key);
    }
  }, [showSubscriptionModal, selectedUser]);
  
  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'danger' | 'warning' | 'info';
    loading?: boolean;
  }>({ open: false, title: '', message: '', onConfirm: () => {}, type: 'info' });
  
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ open: false, title: '', message: '', type: 'info' });

  const API_BASE_URL = getApiUrl();

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (userFilter !== 'all') params.set('status', userFilter);
      if (searchQuery) params.set('search', searchQuery);

      const response = await fetch(`${API_BASE_URL}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch users:', response.status, errorData);
        setAlertDialog({
          open: true,
          title: `Error ${response.status}`,
          message: errorData.error || 'Failed to load users',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token, userFilter, searchQuery]);

  const fetchOnboardingData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (onboardingFilters.role !== 'all') params.set('role', onboardingFilters.role);
      if (onboardingFilters.industry) params.set('industry', onboardingFilters.industry);
      if (onboardingFilters.location) params.set('location', onboardingFilters.location);
      if (onboardingFilters.hasOnboarding !== 'all') params.set('has_onboarding', onboardingFilters.hasOnboarding);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/onboarding-data?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const onboardingRows = Array.isArray(data.data)
          ? data.data
          : Array.isArray(data.users)
            ? data.users
            : [];

        setOnboardingUsers(onboardingRows.map((user: any) => ({
          id: user.userId ?? user.id,
          name: user.name,
          email: user.email,
          createdAt: user.createdAt,
          profile: user.profile ? {
            role: user.profile.role,
            companyName: user.profile.companyName,
            firmName: user.profile.firmName,
            website: user.profile.website,
            location: user.profile.location,
            phone: user.profile.phone,
            stage: user.profile.stage,
            fundingAmount: user.profile.fundingAmount,
            investorType: user.profile.investorType,
            checkSize: user.profile.checkSize,
            industries: user.profile.industries || [],
            stages: user.profile.stages || [],
            businessDescription: user.profile.businessDescription,
            createdAt: user.profile.profileCreatedAt || user.createdAt,
          } : null
        })));
      }
    } catch (error) {
      console.error('Failed to fetch onboarding data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token, onboardingFilters]);

  useEffect(() => {
    if (open) {
      fetchStats();
      fetchUsers();
    }
  }, [open, fetchStats, fetchUsers, userFilter]);
  
  useEffect(() => {
    if (open && activeTab === 'onboarding') {
      fetchOnboardingData();
    }
  }, [open, activeTab, fetchOnboardingData]);

  const approveUser = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    setActionLoading(userId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        fetchUsers();
        fetchStats();
        setAlertDialog({
          open: true,
          title: 'User Approved',
          message: `${user?.email || 'User'} has been approved successfully.`,
          type: 'success'
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Approve failed:', response.status, errorData);
        setAlertDialog({
          open: true,
          title: 'Approval Failed',
          message: errorData.error || 'Failed to approve user',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Failed to approve user:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Network error while approving user',
        type: 'error'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const deleteUser = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    
    setConfirmDialog({
      open: true,
      title: 'Delete User Account',
      message: `Are you sure you want to permanently delete ${user.email}? This action cannot be undone and all their data will be erased.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, loading: true }));
        try {
          const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            setConfirmDialog(prev => ({ ...prev, open: false, loading: false }));
            setAlertDialog({
              open: true,
              title: 'User Deleted',
              message: `${user.email} has been permanently deleted.`,
              type: 'success'
            });
            fetchUsers();
            fetchStats();
          } else {
            const errorData = await response.json().catch(() => ({}));
            setConfirmDialog(prev => ({ ...prev, open: false, loading: false }));
            setAlertDialog({
              open: true,
              title: 'Delete Failed',
              message: errorData.error || 'Failed to delete user',
              type: 'error'
            });
          }
        } catch (error) {
          setConfirmDialog(prev => ({ ...prev, open: false, loading: false }));
          setAlertDialog({
            open: true,
            title: 'Error',
            message: 'Network error while deleting user',
            type: 'error'
          });
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const toggleAdmin = async (userId: number) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/toggle-admin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to toggle admin:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const changePassword = async () => {
    if (!selectedUser || !newPassword || newPassword.length < 6) return;
    
    setActionLoading(selectedUser.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${selectedUser.id}/change-password`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });
      
      if (response.ok) {
        setShowPasswordModal(false);
        setNewPassword('');
        setSelectedUser(null);
        setAlertDialog({
          open: true,
          title: 'Password Changed',
          message: `Password for ${selectedUser.email} has been updated successfully.`,
          type: 'success'
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setAlertDialog({
          open: true,
          title: 'Failed',
          message: errorData.error || 'Failed to change password',
          type: 'error'
        });
      }
    } catch (error) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Network error while changing password',
        type: 'error'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const giveSubscription = async () => {
    if (!selectedUser) {
      console.error('No user selected');
      return;
    }
    
    if (!token) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Not authenticated. Please log in again.',
        type: 'error'
      });
      return;
    }
    
    const selectedPlan = ADMIN_PLAN_OPTIONS.find((p: typeof ADMIN_PLAN_OPTIONS[number]) => p.key === selectedPlanKey) ?? ADMIN_PLAN_OPTIONS[0];
    const planDays = selectedPlan.days;
    // Backend stores canonical monthly key — strip "_yearly" so subscription_plan stays clean.
    const basePlanKey = selectedPlan.key.replace(/_yearly$/, '');

    setActionLoading(selectedUser.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${selectedUser.id}/give-subscription`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days: planDays, plan: basePlanKey })
      });

      if (response.ok) {
        setShowSubscriptionModal(false);
        setSelectedUser(null);
        fetchUsers();
        fetchStats();
        setAlertDialog({
          open: true,
          title: 'Subscription Granted',
          message: `${selectedUser.email} now has access for ${planDays} days.`,
          type: 'success'
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Give subscription failed:', response.status, errorData);
        setAlertDialog({
          open: true,
          title: `Failed (${response.status})`,
          message: errorData.error || 'Failed to grant subscription. Check console for details.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Network error:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Network error while granting subscription. Check console.',
        type: 'error'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const revokeSubscription = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    
    if (!token) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Not authenticated. Please log in again.',
        type: 'error'
      });
      return;
    }
    
    setConfirmDialog({
      open: true,
      title: 'Revoke Subscription',
      message: `Are you sure you want to revoke ${user.email}'s subscription? They will lose access immediately.`,
      type: 'warning',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, loading: true }));
        try {
          const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/revoke-subscription`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
          
          if (response.ok) {
            setConfirmDialog(prev => ({ ...prev, open: false, loading: false }));
            setAlertDialog({
              open: true,
              title: 'Subscription Revoked',
              message: `${user.email}'s subscription has been revoked.`,
              type: 'success'
            });
            fetchUsers();
            fetchStats();
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.error('Revoke subscription failed:', response.status, errorData);
            setConfirmDialog(prev => ({ ...prev, open: false, loading: false }));
            setAlertDialog({
              open: true,
              title: `Failed (${response.status})`,
              message: errorData.error || 'Failed to revoke subscription. Check console.',
              type: 'error'
            });
          }
        } catch (error) {
          console.error('Network error:', error);
          setConfirmDialog(prev => ({ ...prev, open: false, loading: false }));
          setAlertDialog({
            open: true,
            title: 'Error',
            message: 'Network error while revoking subscription. Check console.',
            type: 'error'
          });
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const resetMatchRefreshes = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (!user || !token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/reset-match-refreshes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setAlertDialog({
          open: true,
          title: 'Refreshes Reset',
          message: `${user.email}'s match refresh count has been reset to 0.`,
          type: 'success',
        });
      } else {
        setAlertDialog({ open: true, title: 'Failed', message: 'Could not reset refresh count.', type: 'error' });
      }
    } catch {
      setAlertDialog({ open: true, title: 'Error', message: 'Network error.', type: 'error' });
    }
  };

  const resetMatchCrmAdds = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (!user || !token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/reset-match-crm-adds`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setAlertDialog({
          open: true,
          title: 'Match saves reset',
          message: `${user.email}'s match save count has been reset to 0.`,
          type: 'success',
        });
      } else {
        setAlertDialog({ open: true, title: 'Failed', message: 'Could not reset match save count.', type: 'error' });
      }
    } catch {
      setAlertDialog({ open: true, title: 'Error', message: 'Network error.', type: 'error' });
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={() => onOpenChange(false)}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-background rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-border"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-background to-muted/40 dark:from-slate-900 dark:to-slate-800 border-b border-border p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-[#2B7FFF]" />
                <div>
                  <h2 className="text-2xl font-bold text-foreground dark:text-white">Admin Dashboard</h2>
                  <p className="text-muted-foreground text-sm">Manage users and system</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label
                  className="flex items-center gap-2 cursor-pointer select-none group"
                  title="When on, admins are treated like a regular user with no subscription on the Matches page. Useful for testing the paywall flow."
                >
                  <span className={`text-xs font-medium transition-colors ${
                    simulateNoPlan
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-muted-foreground group-hover:text-foreground dark:group-hover:text-white'
                  }`}>
                    Simulate No Plan
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={simulateNoPlan}
                    onClick={toggleSimulateNoPlan}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full border transition-colors ${
                      simulateNoPlan
                        ? 'bg-amber-500 border-amber-500'
                        : 'bg-muted border-border dark:bg-slate-700 dark:border-slate-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transform transition-transform ${
                        simulateNoPlan ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </label>
                <button
                  onClick={() => onOpenChange(false)}
                  className="text-muted-foreground hover:text-foreground dark:hover:text-white transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mt-6">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'users', label: 'Users', icon: Users },
                { id: 'investors', label: 'Add Investors', icon: BadgeCheck },
                { id: 'analytics', label: 'Analytics', icon: BarChart3 },
                { id: 'onboarding', label: 'Onboarding', icon: UserCheck },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#2B7FFF] text-white'
                      : 'bg-background/80 border border-border text-muted-foreground hover:bg-muted hover:text-foreground dark:bg-muted/50 dark:border-transparent'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {activeTab === 'overview' && stats && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <StatCard
                    icon={Users}
                    label="Total Users"
                    value={stats.totalUsers}
                    color="blue"
                  />
                  <StatCard
                    icon={UserCheck}
                    label="Pending Approvals"
                    value={stats.pendingApprovals}
                    color="amber"
                    alert={stats.pendingApprovals > 0}
                  />
                  <StatCard
                    icon={Shield}
                    label="Admins"
                    value={stats.totalAdmins}
                    color="purple"
                  />
                  <StatCard
                    icon={CreditCard}
                    label="Subscribed"
                    value={stats.subscribedUsers}
                    color="green"
                  />
                  <StatCard
                    icon={Users}
                    label="New Today"
                    value={stats.newUsersToday}
                    color="teal"
                  />
                  <StatCard
                    icon={Clock}
                    label="Failed Logins Today"
                    value={stats.failedLoginsToday}
                    color="red"
                    alert={stats.failedLoginsToday > 10}
                  />
                </div>

                {/* Quick Actions */}
                {stats.pendingApprovals > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-800 rounded-full flex items-center justify-center">
                          <UserCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-amber-900 dark:text-amber-300">
                            {stats.pendingApprovals} user{stats.pendingApprovals > 1 ? 's' : ''} pending approval
                          </h3>
                          <p className="text-sm text-amber-700 dark:text-amber-400">
                            Review and approve new registrations
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          setUserFilter('pending');
                          setActiveTab('users');
                        }}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        Review
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-4">
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value as any)}
                    className="px-4 py-2 rounded-xl border border-border bg-background"
                  >
                    <option value="all">All Users</option>
                    <option value="pending">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="admins">Admins Only</option>
                  </select>
                </div>

                {/* Users Table */}
                {isLoading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No users found
                  </div>
                ) : (
                  <div className="space-y-2">
                    {users.map((user) => (
                      <div
                        key={user.id}
                        className={`flex items-center justify-between p-4 rounded-2xl border ${
                          !user.isApproved
                            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                            : 'bg-muted border-border'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-background rounded-full flex items-center justify-center shadow-sm">
                            <span className="text-lg font-semibold text-muted-foreground">
                              {user.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{user.name || user.email}</span>
                              {user.isAdmin && (
                                <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                  <Shield className="w-3 h-3 mr-1" />
                                  Admin
                                </Badge>
                              )}
                              {!user.isApproved && (
                                <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                  Pending
                                </Badge>
                              )}
                              {user.isSubscribed && (
                                <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                  <BadgeCheck className="w-3 h-3 mr-1" />
                                  Subscribed
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {!user.isApproved && (
                            <Button
                              size="sm"
                              onClick={() => approveUser(user.id)}
                              disabled={actionLoading === user.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              {actionLoading === user.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-1" />
                              )}
                              Approve
                            </Button>
                          )}

                          {/* Edit Subscription Button - Works for ALL users */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowSubscriptionModal(true);
                            }}
                            disabled={actionLoading === user.id}
                            className={user.isSubscribed 
                              ? "text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20" 
                              : "text-green-600 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-900/20"
                            }
                          >
                            <Crown className="w-4 h-4 mr-1" />
                            {user.isSubscribed ? 'Edit Sub' : 'Subscribe'}
                          </Button>

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleAdmin(user.id)}
                            disabled={actionLoading === user.id}
                          >
                            {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                          </Button>

                          {/* Actions Dropdown */}
                          <div className="relative group">
                            <Button
                              size="sm"
                              variant="outline"
                              className="px-2"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                            <div className="absolute right-0 top-full mt-1 w-48 bg-background rounded-xl shadow-lg border border-border hidden group-hover:block z-10">
                              <button
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowPasswordModal(true);
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-sm"
                              >
                                <Key className="w-4 h-4" />
                                Change Password
                              </button>
                              
                              {user.isSubscribed && (
                                <button
                                  onClick={() => revokeSubscription(user.id)}
                                  className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-sm text-red-600"
                                >
                                  <Ban className="w-4 h-4" />
                                  Revoke Subscription
                                </button>
                              )}
                              
                              <button
                                onClick={() => resetMatchRefreshes(user.id)}
                                className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-sm"
                              >
                                <RefreshCw className="w-4 h-4" />
                                Reset Match Refreshes
                              </button>

                              <button
                                onClick={() => resetMatchCrmAdds(user.id)}
                                className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-sm"
                              >
                                <RefreshCw className="w-4 h-4" />
                                Reset match saves
                              </button>

                              <div className="border-t border-border my-1" />

                              <button
                                onClick={() => deleteUser(user.id)}
                                className="w-full text-left px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete Account
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'investors' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Verified Investor Data Collection</h3>
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    Add new investors with verified contact information per protocol standards. 
                    All investors require 2+ independent sources and verified school affiliation.
                  </p>
                </div>
                <InvestorVerificationPanel />
              </div>
            )}

            {activeTab === 'analytics' && <AnalyticsPage />}
            
            {activeTab === 'onboarding' && (
              <OnboardingTab
                users={onboardingUsers}
                filters={onboardingFilters}
                setFilters={setOnboardingFilters}
                selectedUser={selectedOnboardingUser}
                setSelectedUser={setSelectedOnboardingUser}
                isLoading={isLoading}
              />
            )}
          </div>
        </motion.div>

        {/* Change Password Modal */}
        {showPasswordModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => setShowPasswordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-background rounded-2xl p-6 w-full max-w-md border border-border"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-2">Change Password</h3>
              <p className="text-muted-foreground mb-4">For: {selectedUser.email}</p>
              
              <Input
                type="password"
                placeholder="New password (min 6 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mb-4"
              />
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={changePassword}
                  disabled={!newPassword || newPassword.length < 6 || actionLoading === selectedUser.id}
                  className="flex-1 bg-[#2B7FFF] hover:bg-[#1A5FCC]"
                >
                  {actionLoading === selectedUser.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Edit Subscription Modal */}
        {showSubscriptionModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => setShowSubscriptionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-background rounded-2xl p-6 w-full max-w-md border border-border"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-2">
                {selectedUser.isSubscribed ? 'Edit Subscription' : 'Give Subscription'}
              </h3>
              <p className="text-muted-foreground mb-4">For: {selectedUser.email}</p>
              
              {selectedUser.isSubscribed && (
                <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-300">Current Plan</span>
                    <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-0">
                      {selectedUser.subscriptionPlan || 'Active'}
                    </Badge>
                  </div>
                  {selectedUser.subscriptionExpiresAt && (
                    <div className="text-sm text-blue-700 dark:text-blue-400">
                      <span className="font-medium">
                        {Math.max(0, Math.ceil((new Date(selectedUser.subscriptionExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} days
                      </span>
                      {' '}remaining (expires {new Date(selectedUser.subscriptionExpiresAt).toLocaleDateString()})
                    </div>
                  )}
                  <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                    Select a new plan below to change their subscription.
                  </div>
                </div>
              )}
              
              <label className="block text-sm font-medium mb-2">Select Plan</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {ADMIN_PLAN_OPTIONS.map((plan: typeof ADMIN_PLAN_OPTIONS[number]) => {
                  const isSelected = selectedPlanKey === plan.key;
                  const isYearly = plan.key.endsWith('_yearly');
                  return (
                    <button
                      key={plan.key}
                      onClick={() => setSelectedPlanKey(plan.key)}
                      className={`p-3 rounded-lg text-left transition-colors border ${
                        isSelected
                          ? 'bg-[#2B7FFF] text-white border-[#2B7FFF]'
                          : 'bg-background hover:bg-muted border-border'
                      }`}
                    >
                      <div className="font-medium text-sm flex items-center gap-1.5">
                        {plan.name}
                        {isYearly && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            isSelected ? 'bg-white/20 text-white' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                          }`}>
                            Yearly
                          </span>
                        )}
                      </div>
                      <div className={`text-xs ${isSelected ? 'text-white/80' : 'text-muted-foreground'}`}>
                        {plan.displayPrice}{plan.period}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowSubscriptionModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                
                {selectedUser.isSubscribed && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowSubscriptionModal(false);
                      revokeSubscription(selectedUser.id);
                    }}
                    className="flex-1 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Ban className="w-4 h-4 mr-1" />
                    Revoke
                  </Button>
                )}
                
                <Button
                  onClick={giveSubscription}
                  disabled={actionLoading === selectedUser.id}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {actionLoading === selectedUser.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    selectedUser.isSubscribed ? 'Update Plan' : 'Subscribe'
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Alert Dialog */}
        <AlertDialog
          open={alertDialog.open}
          title={alertDialog.title}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
        />

        {/* Confirm Dialog */}
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          type={confirmDialog.type}
          confirmText="Confirm"
          cancelText="Cancel"
          loading={confirmDialog.loading}
          onConfirm={confirmDialog.onConfirm}
          onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        />
      </motion.div>
    </AnimatePresence>
  );
}

function StatCard({ icon: Icon, label, value, color, alert }: { 
  icon: any; 
  label: string; 
  value: number; 
  color: string;
  alert?: boolean;
}) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  };

  return (
    <div className={`p-4 rounded-2xl border ${alert ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700' : 'bg-muted border-border'}`}>
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
}
